<script>
import validations from './helpers/validations';
import { mapGetters } from 'vuex';

import InputNext from 'next/input/Input.vue';
import Icon from 'next/icon/Icon.vue';
import DropdownMenu from 'next/dropdown-menu/DropdownMenu.vue';
import Dropdown from 'next/wgpt/Dropdown/Dropdown.vue';
import ButtonNext from 'next/button/Button.vue';
import Spinner from 'next/spinner/Spinner.vue';
import SourceType from './components/SourceType.vue';
import CreateBotSourceDialog from './components/CreateBotSourceDialog.vue';
import EditBotSourceDialog from './components/EditBotSourceDialog.vue';
import DeleteBotSourceDialog from './components/DeleteBotSourceDialog.vue';

export default {
  components: {
    Icon,
    DropdownMenu,
    Dropdown,
    InputNext,
    ButtonNext,
    Spinner,
    SourceType,
    CreateBotSourceDialog,
    EditBotSourceDialog,
    DeleteBotSourceDialog,
  },
  props: {
    bot: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['sourceSelected', 'search'],
  data() {
    return {
      tableHeaders: [
        this.$t(
          'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.LIST.TABLE_HEADER.ITEM'
        ),
        this.$t(
          'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.LIST.TABLE_HEADER.TYPE'
        ),
      ],
      selectedSource: {},
      contextMenuItems: [
        {
          label: this.$t(
            'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CHUNKS.ITEM.CONTEXT_MENU.EDIT'
          ),
          action: 'edit',
          value: 'edit',
          icon: 'i-lucide-pencil',
        },
        {
          label: this.$t(
            'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CHUNKS.ITEM.CONTEXT_MENU.DELETE'
          ),
          action: 'delete',
          value: 'delete',
          icon: 'i-lucide-trash',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      sources: 'wgptBotSources/getSources',
      uiFlags: 'wgptBotSources/getUIFlags',
    }),
    isEmpty() {
      return this.sources.length === 0;
    },
  },
  watch: {
    selectedSource() {
      this.$emit('sourceSelected', this.selectedSource);
    },
  },
  mounted() {
    const botId = this.$route.params.botId;
    this.$store.dispatch('wgptBotSources/get', botId);
  },
  validations,
  methods: {
    onTabChange(selectedTabIndex) {
      this.selectedTabIndex = selectedTabIndex;
    },
    openAddPopup() {
      this.$refs.createBotSourceDialog.open(this.bot);
    },
    openEditPopup(source) {
      this.$refs.editBotSourceDialog.open(this.bot, source);
    },
    openDeletePopup(source) {
      this.$refs.deleteBotSourceDialog.open(this.bot, source);
    },
    handleContextMenuAction({ action }, toggle, source) {
      if (action === 'edit') {
        this.openEditPopup(source);
      } else if (action === 'delete') {
        this.openDeletePopup(source);
      }
      toggle();
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-4 overflow-hidden">
    <header class="pt-8 px-8 flex items-center gap-4">
      <div class="flex-1">
        <InputNext
          type="search"
          :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
          :custom-input-class="[
            'h-8 border border-n-weak ltr:!pl-8 !py-1 rtl:!pr-8',
          ]"
          @focus="$emit('search')"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
            />
          </template>
        </InputNext>
      </div>

      <div class="w-px h-4 bg-n-strong" />

      <ButtonNext
        :label="
          $t(
            'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.SOURCE.SOURCE_CHUNKS.ADD'
          )
        "
        size="sm"
        @click="openAddPopup"
      />
    </header>

    <div class="px-8 pb-8 overflow-auto">
      <table v-if="!isEmpty" class="mt-3 woot-table">
        <thead class="border-x-none">
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="p-2.5 first:ps-0 last:pe-0 text-start"
          >
            <span
              class="text-sm font-medium normal-case text-ash-900 whitespace-nowrap tracking-normal"
            >
              {{ thHeader }}
            </span>
          </th>
        </thead>
        <tbody class="border-x-none">
          <tr
            v-for="source in sources"
            :key="source.id"
            :class="{ active: selectedSource.id == source.id }"
            class="border-b border-n-slate-2"
          >
            <td
              class="p-2.5 !ps-0 cursor-pointer break-all hover:underline"
              @click="selectedSource = source"
            >
              <span v-if="source.name">{{ source.name }}</span>
              <SourceType v-else :source-type="source.source_type" />
            </td>
            <td class="p-2.5 pl-2">
              <SourceType :source-type="source.source_type" />
            </td>
            <td class="p-2.5 !pe-0 py-1 flex justify-end">
              <Dropdown>
                <template #trigger="{ toggle }">
                  <ButtonNext
                    icon="i-lucide-ellipsis-vertical"
                    color="slate"
                    size="xs"
                    variant="ghost"
                    class="rounded-md group-hover:bg-n-alpha-2"
                    @click="toggle"
                  />
                </template>
                <template #default="{ toggle }">
                  <DropdownMenu
                    class="!static"
                    :menu-items="contextMenuItems"
                    @action="e => handleContextMenuAction(e, toggle, source)"
                  />
                </template>
              </Dropdown>
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="uiFlags.isFetching"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <p
        v-else-if="isEmpty"
        class="p-4 text-sm font-medium text-center text-slate-400 dark:text-slate-400"
      >
        {{ $t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.LIST.404') }}
      </p>
    </div>

    <CreateBotSourceDialog ref="createBotSourceDialog" />

    <EditBotSourceDialog ref="editBotSourceDialog" />

    <DeleteBotSourceDialog ref="deleteBotSourceDialog" />
  </div>
</template>

<style lang="scss" scoped>
.active td > * {
  @apply text-n-brand;
}
</style>
